import { action, observable, makeObservable, computed, reaction } from 'mobx';
import { AppState } from 'src/AppState/AppState';
import { apiGetGlobalLicenseConfigRequest } from 'src/api_openapi/v2/compliance/apiGetGlobalLicenseConfig';
import { apiSetGlobalLicenseConfigRequest } from 'src/api_openapi/v2/compliance/apiSetGlobalLicenseConfig';
import {
    ComplianceLicenseConfigResponseInnerType,
    ComplianceLicenseConfigInfoType,
    ComplianceCurrencyType,
} from 'src/api_openapi/v2/compliance/schemas';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateAmountRequire, validatePositive } from 'src/domains/players/shared/validatorsNew/validateRequire';
import { Amount } from 'src_common/common/amount/Amount';

export class GlobalConfigState {
    private readonly configsResource: Resource<ComplianceLicenseConfigResponseInnerType[]>;
    private readonly disposers: Array<() => void> = [];
    @observable public selectedCurrency: ComplianceCurrencyType | null = null;
    @observable public isSaving: boolean = false;
    @observable public errorMessage: string | null = null;

    public readonly maxSingleWithdrawalTrigger: FormInputState<string, Amount>;
    public readonly maxSingleDepositTrigger: FormInputState<string, Amount>;
    public readonly maxTotalWithdrawalTriggerUnderThreshold: FormInputState<string, Amount>;
    public readonly maxTotalWithdrawalTriggerOverThreshold: FormInputState<string, Amount>;
    public readonly maxTotalDepositTriggerUnderThreshold: FormInputState<string, Amount>;
    public readonly maxTotalDepositTriggerOverThreshold: FormInputState<string, Amount>;
    public readonly drMaxTotalWithdrawalTriggerUnderThreshold: FormInputState<string, Amount>;
    public readonly drMaxTotalWithdrawalTriggerOverThreshold: FormInputState<string, Amount>;
    public readonly drMaxTotalDepositTriggerUnderThreshold: FormInputState<string, Amount>;
    public readonly drMaxTotalDepositTriggerOverThreshold: FormInputState<string, Amount>;

    public constructor(private readonly appState: AppState) {
        this.maxSingleWithdrawalTrigger = FormInputState.new('').map(validateAmountRequire).map(validatePositive);
        this.maxSingleDepositTrigger = FormInputState.new('').map(validateAmountRequire).map(validatePositive);
        this.maxTotalWithdrawalTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.maxTotalWithdrawalTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.maxTotalDepositTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.maxTotalDepositTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.drMaxTotalWithdrawalTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.drMaxTotalWithdrawalTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.drMaxTotalDepositTriggerUnderThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);
        this.drMaxTotalDepositTriggerOverThreshold = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validatePositive);

        this.configsResource = new Resource(async () => {
            const { gatewayUrl } = this.appState.common.appEnvParams;
            const response = await apiGetGlobalLicenseConfigRequest(
                gatewayUrl,
                {},
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                return response.body.config;
            } else {
                throw new Error(`Failed to fetch config: status ${response.status}`);
            }
        });
        makeObservable(this);

        this.disposers.push(
            reaction(
                () => this.isLoading,
                (isLoading) => {
                    if (this.selectedCurrency === null && !isLoading) {
                        this.selectCurrency('GBP');
                    }
                },
                { fireImmediately: true }
            )
        );

        this.disposers.push(
            reaction(
                () => this.selectedConfig,
                (config) => {
                    this.loadConfigToForm(config);
                },
                { fireImmediately: true }
            )
        );
    }

    public dispose(): void {
        this.disposers.forEach((dispose) => dispose());
    }

    @computed public get configs(): ComplianceLicenseConfigResponseInnerType[] {
        const result = this.configsResource.get();

        if (result.type === 'ready') {
            return result.value;
        }

        if (result.type === 'error') {
            this.errorMessage = result.message;
        }

        return [];
    }

    @computed public get isLoading(): boolean {
        return this.configsResource.get().type === 'loading';
    }

    public async refresh(): Promise<void> {
        await this.configsResource.refresh();
    }

    @action public selectCurrency = (currency: ComplianceCurrencyType): void => {
        this.selectedCurrency = currency;
        const config = this.configs.find((c) => c.currency === currency);
        this.loadConfigToForm(config ?? null);
    };

    @action public loadConfigToForm = (config: ComplianceLicenseConfigResponseInnerType | null): void => {
        if (config === null) {
            this.resetForm();
            return;
        }

        this.maxSingleWithdrawalTrigger.setValue(config.maxSingleWithdrawalTrigger);
        this.maxSingleDepositTrigger.setValue(config.maxSingleDepositTrigger);
        this.maxTotalWithdrawalTriggerUnderThreshold.setValue(config.maxTotalWithdrawalTriggerUnderThreshold);
        this.maxTotalWithdrawalTriggerOverThreshold.setValue(config.maxTotalWithdrawalTriggerOverThreshold);
        this.maxTotalDepositTriggerUnderThreshold.setValue(config.maxTotalDepositTriggerUnderThreshold);
        this.maxTotalDepositTriggerOverThreshold.setValue(config.maxTotalDepositTriggerOverThreshold);
        this.drMaxTotalWithdrawalTriggerUnderThreshold.setValue(config.drMaxTotalWithdrawalTriggerUnderThreshold);
        this.drMaxTotalWithdrawalTriggerOverThreshold.setValue(config.drMaxTotalWithdrawalTriggerOverThreshold);
        this.drMaxTotalDepositTriggerUnderThreshold.setValue(config.drMaxTotalDepositTriggerUnderThreshold);
        this.drMaxTotalDepositTriggerOverThreshold.setValue(config.drMaxTotalDepositTriggerOverThreshold);
    };

    @action public resetForm = (): void => {
        this.maxSingleWithdrawalTrigger.reset();
        this.maxSingleDepositTrigger.reset();
        this.maxTotalWithdrawalTriggerUnderThreshold.reset();
        this.maxTotalWithdrawalTriggerOverThreshold.reset();
        this.maxTotalDepositTriggerUnderThreshold.reset();
        this.maxTotalDepositTriggerOverThreshold.reset();
        this.drMaxTotalWithdrawalTriggerUnderThreshold.reset();
        this.drMaxTotalWithdrawalTriggerOverThreshold.reset();
        this.drMaxTotalDepositTriggerUnderThreshold.reset();
        this.drMaxTotalDepositTriggerOverThreshold.reset();
    };

    @action private setError = (message: string | null): void => {
        this.errorMessage = message;
    };

    @action private setSaving = (saving: boolean): void => {
        this.isSaving = saving;
    };

    @action public updateConfig = async (): Promise<void> => {
        if (this.selectedCurrency === null) {
            this.setError('No currency selected');
            return;
        }

        this.maxSingleWithdrawalTrigger.setAsVisited();
        this.maxSingleDepositTrigger.setAsVisited();
        this.maxTotalWithdrawalTriggerUnderThreshold.setAsVisited();
        this.maxTotalWithdrawalTriggerOverThreshold.setAsVisited();
        this.maxTotalDepositTriggerUnderThreshold.setAsVisited();
        this.maxTotalDepositTriggerOverThreshold.setAsVisited();
        this.drMaxTotalWithdrawalTriggerUnderThreshold.setAsVisited();
        this.drMaxTotalWithdrawalTriggerOverThreshold.setAsVisited();
        this.drMaxTotalDepositTriggerUnderThreshold.setAsVisited();
        this.drMaxTotalDepositTriggerOverThreshold.setAsVisited();

        if (
            this.maxSingleWithdrawalTrigger.result.value.type === 'error' ||
            this.maxSingleDepositTrigger.result.value.type === 'error' ||
            this.maxTotalWithdrawalTriggerUnderThreshold.result.value.type === 'error' ||
            this.maxTotalWithdrawalTriggerOverThreshold.result.value.type === 'error' ||
            this.maxTotalDepositTriggerUnderThreshold.result.value.type === 'error' ||
            this.maxTotalDepositTriggerOverThreshold.result.value.type === 'error' ||
            this.drMaxTotalWithdrawalTriggerUnderThreshold.result.value.type === 'error' ||
            this.drMaxTotalWithdrawalTriggerOverThreshold.result.value.type === 'error' ||
            this.drMaxTotalDepositTriggerUnderThreshold.result.value.type === 'error' ||
            this.drMaxTotalDepositTriggerOverThreshold.result.value.type === 'error'
        ) {
            this.setError('Please fix all validation errors');
            return;
        }

        try {
            this.setSaving(true);
            this.setError(null);

            const updatedConfig: ComplianceLicenseConfigInfoType = {
                universe: this.appState.common.appEnvParams.universe_api,
                currency: this.selectedCurrency,
                maxSingleWithdrawalTrigger: this.maxSingleWithdrawalTrigger.value,
                maxSingleDepositTrigger: this.maxSingleDepositTrigger.value,
                maxTotalWithdrawalTriggerUnderThreshold: this.maxTotalWithdrawalTriggerUnderThreshold.value,
                maxTotalWithdrawalTriggerOverThreshold: this.maxTotalWithdrawalTriggerOverThreshold.value,
                maxTotalDepositTriggerUnderThreshold: this.maxTotalDepositTriggerUnderThreshold.value,
                maxTotalDepositTriggerOverThreshold: this.maxTotalDepositTriggerOverThreshold.value,
                drMaxTotalWithdrawalTriggerUnderThreshold: this.drMaxTotalWithdrawalTriggerUnderThreshold.value,
                drMaxTotalWithdrawalTriggerOverThreshold: this.drMaxTotalWithdrawalTriggerOverThreshold.value,
                drMaxTotalDepositTriggerUnderThreshold: this.drMaxTotalDepositTriggerUnderThreshold.value,
                drMaxTotalDepositTriggerOverThreshold: this.drMaxTotalDepositTriggerOverThreshold.value,
            };

            const { gatewayUrl } = this.appState.common.appEnvParams;
            const response = await apiSetGlobalLicenseConfigRequest(
                gatewayUrl,
                { requestBody: updatedConfig },
                { authorization: `Bearer ${this.appState.common.session.currentJwt}` }
            );

            if (response.status === 200) {
                await this.refresh();
            } else {
                this.setError(`Failed to update config: status ${response.status}`);
            }
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Unknown error occurred');
            console.error('Error updating global config:', error);
        } finally {
            this.setSaving(false);
        }
    };

    @computed public get selectedConfig(): ComplianceLicenseConfigResponseInnerType | null {
        if (this.selectedCurrency === null) {
            return null;
        }
        return this.configs.find((c) => c.currency === this.selectedCurrency) ?? null;
    }
}
